import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useMemo } from "react";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { labelInternalToUI, labelMappings } from "config/LabelMapping";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { AssesmentsHistory } from "types/assesments";
import { convertUTCtoLocalReadableDate } from "utils/utilityFunctions";

import { generateBackgroundColor } from "../utils";

interface AssesmentHistoryProps {
  data: any;
  open: boolean;
  onClose: () => void;
}

export function AssesmentHistoryModal(props: AssesmentHistoryProps) {
  const { data, open, onClose } = props;

  const imageUrl = `${JANZU_URL}api/vizproc/image/?id=`;

  const assesmentsUrl = `${JANZU_URL}reef-api/assessments-history?labeler_username=${data.xLabel}&detector_id=${data.detector_id}`;
  const { data: assesmentHistory, loading: loadingAssesmentsHistory } = useFetchCustom<AssesmentsHistory[]>(
    assesmentsUrl,
    { cachePolicy: CachePolicies.NO_CACHE },
    []
  );

  const filterAssesmentsHistory = useMemo(() => {
    if (assesmentHistory && assesmentHistory?.length > 0) {
      return assesmentHistory.filter((assesment) => assesment.human_readable_value !== null);
    }
    return [];
  }, [assesmentHistory]);

  const valueFixed =
    data && data.value !== null && data.value !== undefined && !Number.isNaN(data.value) ? data.value.toFixed(2) : null;

  const renderDate = (date: string) => convertUTCtoLocalReadableDate(date);

  return (
    <Dialog
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullWidth
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        style: {
          minHeight: "70vh",
          maxHeight: "70vh",
          maxWidth: "1000px",
          minWidth: "200px",
          backgroundColor: "#FFFFFF",
        },
      }}
      scroll="paper"
    >
      <DialogTitle id="scroll-dialog-title" className="flex justify-between items-center p-1">
        <Typography variant="detail" fontWeight="bold" className="text-[#006DD9] ml-3">
          {data?.label_prompt}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon className="text-[#006DD9]" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="px-4">
        <Box className="flex flex-col gap-1">
          <Typography variant="detail" className="flex text-gray-600 items-center gap-1">
            <span className="font-bold">ID:</span> {data?.detector_id}
          </Typography>
        </Box>
        <Divider className="my-2" />
        <Box className="flex flex-col gap-2">
          <Typography variant="subtitle" fontWeight="bold" className="text-black">
            {data?.xLabel}
          </Typography>
          <Typography variant="detail" fontWeight="semibold" className="flex text-gray-600 items-center gap-1">
            Queries labeled on this detector: <span className="font-medium text-[#006DD9]">{data?.total}</span>
          </Typography>
        </Box>
        <Divider className="my-2" />
        <Box className="flex flex-col gap-4">
          <Typography fontWeight="semibold" className="text-[#49494D]">
            Assesment Score
          </Typography>
          <Box className="flex justify-center items-center gap-2">
            <Typography
              fontWeight="semibold"
              className="text-black p-3 rounded-md"
              sx={{ backgroundColor: generateBackgroundColor(data.value) }}
            >
              {valueFixed}
            </Typography>
            <Box className="flex flex-col gap-2">
              <Typography variant="detail" fontWeight="semibold" className="flex text-gray-600 items-center gap-1">
                Total assessments: <span className="font-medium text-[#006DD9]">{data?.total}</span>
              </Typography>
              <Typography variant="detail" fontWeight="semibold" className="flex text-gray-600 items-center gap-1">
                Correct assessments: <span className="font-medium text-[#006DD9]">{data?.correct}</span>
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider className="mt-4 mb-2" />

        <Box className="flex flex-col gap-2">
          <Typography fontWeight="semibold" className="text-[#49494D]">
            Assessment History:{" "}
            {assesmentHistory && assesmentHistory.length > 0 && (
              <>
                <b>{filterAssesmentsHistory?.length}</b> of <b>{assesmentHistory?.length}</b> assessments are available
                for you to review
              </>
            )}
          </Typography>

          {loadingAssesmentsHistory ? (
            <Box className="flex justify-center py-4">
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {filterAssesmentsHistory && filterAssesmentsHistory?.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Thumbnail</TableCell>
                        <TableCell align="center">Timestamp</TableCell>
                        <TableCell align="center">Ground Truth Answer</TableCell>
                        <TableCell align="center">Labeler Answer</TableCell>
                        <TableCell align="center">Disagreement Resolution</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filterAssesmentsHistory.map((row) => (
                        <TableRow
                          key={row.id}
                          className={
                            row?.ground_truth_label?.class_name !== row?.human_readable_value
                              ? "bg-red-300"
                              : "bg-inherit"
                          }
                          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            <img
                              alt={row.image_id}
                              src={`${imageUrl}${row.image_id}`}
                              style={{ height: "70px", width: "100%", objectFit: "contain" }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {row?.ground_truth_label ? renderDate(row.ground_truth_label.created_at) : ""}
                          </TableCell>
                          <TableCell align="center">
                            {row.ground_truth_label
                              ? labelInternalToUI(labelMappings, row.ground_truth_label.class_name)
                              : "None"}
                          </TableCell>
                          <TableCell align="center">
                            {row.ground_truth_label
                              ? labelInternalToUI(labelMappings, row.human_readable_value)
                              : "None"}
                          </TableCell>
                          <TableCell align="center" className="capitalize">
                            {row?.disagreement_state === null
                              ? "None"
                              : (row.disagreement_state as string).toLowerCase()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box className="flex justify-center py-4">No rows</Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
